import { format } from "date-fns";
import { formatInTimeZone, getTimezoneOffset } from "date-fns-tz";

let profileTimezone: string | undefined;
let profileTimezoneChosen = false;

const isValidTimezone = (timezone: string) => {
  try {
    new Intl.DateTimeFormat("en", { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

// Undefined when the browser reports a timezone its own clock is not on (e.g. "UTC" with the
// clock at UTC-8): then neither can be trusted
export function getBrowserTimezone(date: Date = new Date()): string | undefined {
  const reported = Intl.DateTimeFormat().resolvedOptions()?.timeZone;
  if (!reported || !isValidTimezone(reported)) {
    return undefined;
  }
  const reportedOffsetMinutes = getTimezoneOffset(reported, date) / 1000 / 60;
  return reportedOffsetMinutes === -date.getTimezoneOffset() ? reported : undefined;
}

export function setProfileTimezone(timezone?: string | null, chosenAt?: unknown): void {
  profileTimezone = timezone && isValidTimezone(timezone) ? timezone : undefined;
  profileTimezoneChosen = Boolean(chosenAt);
}

// One she chose herself first, then the browser's, so a member who travels sees local times
export function getTimezone(): string {
  if (profileTimezoneChosen && profileTimezone) {
    return profileTimezone;
  }
  return (
    getBrowserTimezone() ??
    profileTimezone ??
    Intl.DateTimeFormat().resolvedOptions()?.timeZone ??
    "UTC"
  );
}

export const formatInUserTimezone = (date: Date | string | number, formatType: string) =>
  formatInTimeZone(date, getTimezone(), formatType);

const timeZoneOffset = (currentTimezone: string, forDate?: Date) => {
  const offsetInMinutes = getTimezoneOffset(currentTimezone, forDate) / 1000 / 60;
  const sign = offsetInMinutes > 0 ? "+" : "-";
  const hours = Math.floor(Math.abs(offsetInMinutes) / 60);
  const differenceInMinutes = Math.abs(offsetInMinutes) - hours * 60;
  const minutes = differenceInMinutes > 0 ? differenceInMinutes : 0;
  const separator = ":";
  return minutes > 0 ? `(UTC${sign}${hours}${separator}${minutes})` : `(UTC${sign}${hours})`;
};

// `date` must already be converted with `toZonedTime(…, getTimezone())`
export const formatTimeWithOffset = (date: Date, formatType: string) => {
  const currentTimezone = getTimezone();

  return `${format(date, formatType)} ${timeZoneOffset(currentTimezone, date)}`;
};
